import React, {useState, useEffect, useContext} from 'react';
import {
    Box, FormControl, Input, Text, Heading, useToast, Button, HStack, Icon
} from '@chakra-ui/react';
import { FaLaptop, FaEnvelope } from 'react-icons/fa';
import {useTranslation} from "react-i18next";
import {ConfigContext} from "./utils/Config";

const MyMaxBidLimit = ({ accessToken, saleUuid, userUuid }) => {
    const toast = useToast();
    const { t } = useTranslation();
    const [allBidderInfo, setAllBidderInfo] = useState(null);
    const [pendingUpdates, setPendingUpdates] = useState({});
    const [isSubmitting, setIsSubmitting] = useState({});
    const config = useContext(ConfigContext);

    useEffect(() => {
        fetchAllCreditInfo();
    }, [accessToken, saleUuid, userUuid]);

    const fetchAllCreditInfo = async () => {
        try {
            const response = await fetch(`${config.backendUrl}/api/v1.0/bidder_ids?user_uuid=${userUuid}&sale_uuid=${saleUuid}&access_token=${accessToken}`);
            if (!response.ok) return;
            const data = await response.json();
            if (data.count === 0) return;
            
            // Filter for only website and mail types, then sort
            const filteredBidders = data.data.filter(bidder => 
                bidder.bidder_type === 'website' || bidder.bidder_type === 'mail'
            );
            
            // Sort bidders: website first, then mail sorted by number
            const sortedBidders = filteredBidders.sort((a, b) => {
                if (a.bidder_type === 'website' && b.bidder_type !== 'website') return -1;
                if (a.bidder_type !== 'website' && b.bidder_type === 'website') return 1;
                return parseInt(a.bidder_number) - parseInt(b.bidder_number);
            });
            
            setAllBidderInfo(sortedBidders);
            setPendingUpdates({});
        } catch (error) {
            toast({
                title: t('myCredit.error'),
                description: error.title,
                status: 'error',
                duration: 9000,
                isClosable: true,
            });
        }
    };

    const handleMaxBidUpdate = async (bidderId, newMaxBid) => {
        setIsSubmitting(prev => ({...prev, [bidderId]: true}));
        try {
            const response = await fetch(`${config.backendUrl}/api/v1.0/bidder_ids/${bidderId}?access_token=${accessToken}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    max_bid: newMaxBid
                }),
            });
    
            if (!response.ok) throw new Error('Failed to update max bid');
    
            await fetchAllCreditInfo();
            toast({
                title: t('myCredit.maxBidUpdated'),
                status: 'success',
                duration: 3000,
            });
        } catch (error) {
            toast({
                title: t('myCredit.error'),
                description: error.message,
                status: 'error',
                duration: 3000,
            });
        } finally {
            setIsSubmitting(prev => ({...prev, [bidderId]: false}));
        }
    };

    const getBidderTypeIcon = (bidderType) => {
        return bidderType === 'website' ? 
            <Icon as={FaLaptop} mr={2} /> : 
            <Icon as={FaEnvelope} mr={2} />;
    };

    return (
        allBidderInfo && (
            <Box p={4} borderWidth="1px" borderRadius="lg" mb={4}>
                <Heading as="h3" size="sm" mb={4}>{t('myCredit.maxBidLimits')}</Heading>
                
                {/* Help text */}
                <Text mb={4} fontSize="sm" color="gray.600">
                    {t('myCredit.maxBidLimitsHelp')}
                </Text>

                {allBidderInfo.map((bidder) => (
                    <Box key={bidder.id} mb={3}>
                        <FormControl>
                            <HStack spacing={2} align="center">
                                <HStack flex="1">
                                    {getBidderTypeIcon(bidder.bidder_type)}
                                    <Text>
                                        {t('myCredit.bidderNumber', {number: bidder.bidder_number})} ({bidder.bidder_type})
                                    </Text>
                                </HStack>
                                
                                <Text whiteSpace="nowrap">{t('myCredit.currentLimit')}</Text>
                                <Input
                                    className="maxbidinput"
                                    type="number"
                                    w="200px"                                    
                                    value={pendingUpdates[bidder.id] !== undefined 
                                        ? pendingUpdates[bidder.id] 
                                        : bidder.max_bid === '-1' ? '' : bidder.max_bid}
                                    placeholder={t('myCredit.unlimited')}
                                    onChange={(e) => setPendingUpdates(prev => ({
                                        ...prev,
                                        [bidder.id]: e.target.value
                                    }))}
                                />
                                <Button
                                    colorScheme="blue"
                                    isLoading={isSubmitting[bidder.id]}
                                    onClick={() => handleMaxBidUpdate(bidder.id, pendingUpdates[bidder.id] || '-1')}
                                    isDisabled={pendingUpdates[bidder.id] === undefined || 
                                    pendingUpdates[bidder.id] === (bidder.max_bid === '-1' ? '' : bidder.max_bid)}
                                >
                                    {t('myCredit.setBiddingLimit')}
                                </Button>
                            </HStack>
                        </FormControl>
                    </Box>
                ))}
            </Box>
        )
    );
};

export default MyMaxBidLimit;